import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// Add token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle blob errors properly
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // For blob responses, error.response might not have data in the expected format
    if (error.config && error.config.responseType === 'blob') {
      // Don't try to access responseText for blob responses
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// Helper function to bypass recorder issues with fetch
export const safeFetch = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    ...options.headers,
    'Authorization': token ? `Bearer ${token}` : '',
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  return response;
};

function PrivateRoute({ children, adminOnly = false }) {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function App() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Load saved language on app start
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
    
    // Load and apply design settings
    loadDesignSettings();
  }, [i18n]);
  
  const loadDesignSettings = async () => {
    try {
      const response = await axiosInstance.get("/design-settings");
      const settings = response.data;
      
      // Apply design settings via CSS variables
      if (settings.background_color) {
        document.documentElement.style.setProperty('--bg-color', settings.background_color);
      }
      if (settings.button_color) {
        document.documentElement.style.setProperty('--button-color', settings.button_color);
      }
      
      // Store logo URL for access in components
      if (settings.logo_url) {
        localStorage.setItem('logo_url', settings.logo_url);
      } else {
        localStorage.removeItem('logo_url');
      }
    } catch (error) {
      console.error("Error loading design settings:", error);
    }
  };
  
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly={true}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
